const tooltips = {
  conservation:
    "<strong>PhyloP</strong> scores measure evolutionary conservation at individual alignment sites. The scores " +
    "are interpreted as follows compared to the evolution expected under neutral drift: positive scores (max 3.0) mean " +
    "conserved positions and negative scores (min -14.0) indicate positive selection. PhyloP scores are useful to " +
    "evaluate signatures of selection at particular nucleotides or classes of nucleotides (e.g., third codon positions, " +
    "or first positions of miRNA target sites).<br>" +
    "<strong>PhastCons</strong> estimates the probability that each nucleotide belongs to a conserved element, based on " +
    "the multiple alignment. The phastCons scores represent probabilities of negative selection and range between 0 " +
    "(non-conserved) and 1 (highly conserved).<br>" +
    "<strong>Genomic Evolutionary Rate Profiling (GERP)</strong> score estimate the level of conservation of positions." +
    " Scores ≥ 2 indicate evolutionary constraint to and ≥ 3 indicate purifying selection.",
  populationFrequencies: `<strong>1000 Genomes</strong> Only considers variants whose observed allelic frequency in the 1000 genomes phase 3 database
                is below (or above) the defined value. Genome-wide allelic frequencies were obtained from more than 2.500 genomes.<br><br>
              <strong>gnomAD Genomes</strong> Only considers variants whose observed allelic frequency in the gnomAD Genomes database is
                below (or above) the defined value. Frequencies were calculated from about 15,000 unrelated individuals`,
  proteinSubstitutionScore:
    "<strong>SIFT score:</strong> Choose either a Tolerated/Deleterious qualitative score or provide below a " +
    "quantitative impact value. SIFT scores <0.05 are considered deleterious. " +
    "<strong>Polyphen:</strong> Choose, either a Benign/probably damaging qualitative score or provide below a " +
    "quantitative impact value. Polyphen scores can be Benign (<0.15), Possibly damaging (0.15-0.85) or Damaging (>0.85)",
  study: "Only considers variants from the selected studies",
  variant: "Filter out variants using the format 'chr:pos:ref:alt'",
  region: "Filter out variants falling outside the genomic interval(s) defined",
  feature:
    "Filter out variants falling outside the genomic features (gene, transcript, SNP, etc.) defined",
  diseasePanels:
    "Filter out variants falling outside the genomic intervals (typically genes) defined by the panel(s) chosen",
  biotype: "Filtering out variants without the selected gene biotypes terms",
  roleInCancer:
    "Filtering out variants by the different role in cancer of the genes",
  type: "Only considers variants of the selected type",
  consequenceTypeSelect:
    "Filtering out variants without the selected Consequence type SO terms",
  go:
    "The Gene Ontology (GO) knowledgebase is the world's largest source of information on the functions of genes." +
    "This knowledge is both human-readable and machine-readable, and is a foundation for computational analysis of large-scale molecular biology and genetics experiments in biomedical research.",
  hpo: "The Human Phenotype Ontology (HPO) provides a standardized vocabulary of phenotypic abnormalities encountered in human disease.",
  clinvar:
    "Filter out variants falling outside the genomic features (gene, transcript, SNP, etc.) defined",
  fullTextSearch:
    "Filter out variants that do not match with the IDs or the full text provided",
  cadd:
    "Raw values have relative meaning, with higher values indicating that a variant is more likely to be " +
    "simulated (or not observed) and therefore more likely to have deleterious effects. If discovering causal variants " +
    "within an individual, or small groups, of exomes or genomes te use of the scaled CADD score is recommended",
  cohort: "Filter variants by the cohort alternate allele frequency",
  sample:
    "Select variants by sample genotypes and other criteria",
  clinical:
    "Filter out variants without the selected annotations for a given clinical database",
  variantCallerFile:
    "Filter out variants that do not satisfy the selected conditions for the displayed VCF info fields",
  variantCallerSample:
    "Filter out variants that do not satisfy the selected conditions for the displayed VCF format fields",
  vcfFile: "Filter out variants not in the VCF files selected",
};

export const filterconfigs = {
  title: "Filter",
  activefields: {
    alias: {},
    complexFields: [],
    hiddenFields: [],
  },
  panels: [
    {
      title: "Genomic",
      collapsed: false,
      fields: [
        // {
        //     id: "variant",
        //     title: "Variant ID",
        //     tooltip: tooltips.variant
        // },
        {
          id: "region",
          title: "Genomic Location",
          tooltip: tooltips.region,
          row: 3,
          placeholder:
            "1:1-X:100000, chr3:444-chr5:55555, chrX:100-chrY:200000, ...",
          validationMessage: "Invalid input format. Please use the format: [chr]chromosome:start-end, e.g., 1:1-100000, chr3:444-55555, chrX:100-200000.",
          Pattern: (() => {
            // Break down the regex into readable parts
            const chromosome = `(?:chr)?(?:[1-9]|1[0-9]|2[0-2]|X|Y)`;
            const position = `[1-9]\\d*`;

            // Support both formats:
            // Old: chr:pos-pos (e.g., "1:100-200")
            // New: chr:pos-chr:pos (e.g., "1:100-X:200")
            const oldFormat = `${chromosome}:\\s*${position}\\s*-\\s*${position}`;
            const newFormat = `${chromosome}:\\s*${position}\\s*-\\s*${chromosome}:\\s*${position}`;

            const singleRange = `(?:${newFormat}|${oldFormat})`;
            const multipleRanges = `${singleRange}(?:\\s*,\\s*${singleRange})*`;
            return new RegExp(`^${multipleRanges}$`);
          })(),
        },
        {
          id: "feature",
          title: "Gene Symbol",
          tooltip: tooltips.feature,
          row: 8,
          placeholder: "BRCA2,TP53,...",
        },
        {
          id: "biotype",
          title: "Gene Biotype",
          biotypes: [],
          tooltip: tooltips.biotype,
        },
      ],
    },
    {
      title: "Consequence Type",
      collapsed: true,
      fields: [
        {
          id: "consequence-type",
          title: "Select SO terms",
          tooltip: tooltips.consequenceTypeSelect,
          params: {
            consequenceTypes: [],
          },
        },
      ],
    },
    {
      title: "Population Frequency",
      collapsed: true,
      fields: [
        {
          id: "populationFrequencyAF",
          title: "Allele Frequency",
          placeholder: "0.1-0.5,...",
        },
        {
          id: "populationFrequencyAMR_AF",
          title: "AMR Allele Frequency",
          placeholder: "0.1-0.5,...",
        },
        {
          id: "populationFrequencyEAS_AF",
          title: "EAS Allele Frequency",
          placeholder: "0.1-0.5,...",
        },
        {
          id: "populationFrequencyAFR_AF",
          title: "AFR Allele Frequency",
          placeholder: "0.1-0.5,...",
        },
        {
          id: "populationFrequencyEUR_AF",
          title: "EUR Allele Frequency",
          placeholder: "0.1-0.5,...",
        },
      ],
    },
    {
      title: "Clinical",
      collapsed: true,
      fields: [
        {
          id: "diseasePanels",
          title: "Disease Panels",
          tooltip: tooltips.diseasePanels,
        },
        {
          id: "clinical-annotation",
          title: "Clinical Annotation",
          tooltip: tooltips.clinical,
        },
        {
          id: "role-in-cancer",
          title: "Gene Role In Cancer",
          tooltip: tooltips.roleInCancer,
          disabled: false,
          message: {
            visible: true,
            text: "Gene Role in Cancer filter is only available from OpenCGA 2.6.0",
          },
          params: {
            rolesInCancer: [],
          },
        },
        {
          id: "fullTextSearch",
          title:
            "Full-text search on HPO, ClinVar, protein domains or keywords. Some OMIM and Orphanet IDs are also supported",
          tooltip: tooltips.fullTextSearch,
        },
      ],
    },
    {
      title: "Phenotype",
      collapsed: true,
      fields: [
        {
          id: "go",
          title: "GO Accessions (max. 100 terms)",
          tooltip: tooltips.go,
        },
        {
          id: "hpo",
          title: "HPO Accessions",
          tooltip: tooltips.hpo,
        },
      ],
    },
    {
      title: "Deleteriousness",
      collapsed: true,
      fields: [
        {
          id: "proteinSubstitutionScore",
          title: "Protein Substitution Score",
          tooltip: tooltips.proteinSubstitutionScore,
        },
        {
          id: "cadd",
          title: "CADD",
          tooltip: tooltips.cadd,
        },
      ],
    },
  ],
  examples: [],
  result: {
    grid: {},
  },
};

// Use runtime config if available (from CDK deployment), otherwise fallback to development values
const getRuntimeConfig = () => {
  if (typeof window !== 'undefined' && window.RUNTIME_CONFIG) {
    return window.RUNTIME_CONFIG;
  }
  throw new Error('Runtime configuration not found. Please ensure the application is deployed correctly.');
};

const config = getRuntimeConfig();

export const frontend = config.frontendUrl;
export const endpoint = config.apiEndpoint;
export const authEndpoint = config.authEndpoint;
export const filesEndpoint = config.filesEndpoint;
export const clientId = config.userPoolClientId;
export const cognitoDomain = config.cognitoDomain;
export const cognitoAuthConfig = {
  authority: `https://cognito-idp.${config.cognitoRegion}.amazonaws.com/${config.userPoolId}`,
  client_id: config.userPoolClientId,
  redirect_uri: config.frontendUrl,
  response_type: "code",
  scope: "email openid",
  onSigninCallback: () => {
    window.history.replaceState({}, document.title, window.location.pathname);
  },
};
